// File: src/components/HelpView.js
// Help documentation component

import React from 'react';
import { X, Trophy, Users, Calendar, BarChart3, Settings, Download, FileSpreadsheet, HelpCircle, Layers, ClipboardList } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function HelpView({ onClose, isAdmin = false }) {
  const { t } = useLanguage();

  const HelpSection = ({ icon: Icon, title, children }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-accent" />
        <h3 className="text-xl font-bold text-ink">{title}</h3>
      </div>
      <div className="text-ink-muted space-y-2">
        {children}
      </div>
    </div>
  );

  const Step = ({ number, children }) => (
    <div className="flex gap-3 mb-2">
      <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
        {number}
      </span>
      <p className="flex-1">{children}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-line">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-8 h-8 text-accent" />
            <h2 className="text-3xl font-bold text-ink">{t('help')}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-2 rounded-lg hover:bg-paper-alt transition-colors"
            title={t('close')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 flex-1">
          {/* Public Help Content */}
          {!isAdmin && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-ink mb-4">{t('helpPublicTitle')}</h2>
                <p className="text-ink-muted mb-6">{t('helpPublicDescription')}</p>
              </div>

              <HelpSection icon={Trophy} title={t('helpViewingStandings')}>
                <Step number={1}>{t('helpSelectCountry')}</Step>
                <Step number={2}>{t('helpSelectYear')}</Step>
                <Step number={3}>{t('helpSelectSeries')}</Step>
                <Step number={4}>{t('helpSelectClass')}</Step>
                <Step number={5}>{t('helpViewTabs')}</Step>
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg p-3 mt-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>💡 {t('tip')}</strong> {t('helpStandingsTip')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={BarChart3} title={t('helpUnderstandingStandings')}>
                <p><strong>{t('championshipStandings')}:</strong> {t('helpChampionshipExplanation')}</p>
                <p><strong>{t('qualificationStandings')}:</strong> {t('helpQualificationExplanation')}</p>
                <p><strong>{t('juniorChampionship')}:</strong> {t('helpJuniorExplanation')}</p>
                <p><strong>{t('seniorChampionship')}:</strong> {t('helpSeniorExplanation')}</p>
              </HelpSection>

              <HelpSection icon={Calendar} title={t('helpRaceResults')}>
                <p>{t('helpRaceResultsDescription')}</p>
              </HelpSection>

              <HelpSection icon={Calendar} title={t('helpRaceCalendar')}>
                <p>{t('helpRaceCalendarDescription')}</p>
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg p-3 mt-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>🚦 {t('tip')}</strong> {t('helpCalendarColours')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={ClipboardList} title={t('helpEntering')}>
                <p className="mb-3">{t('helpEnteringDescription')}</p>
                <Step number={1}>{t('helpEnterPickClasses')}</Step>
                <Step number={2}>{t('helpEnterDetails')}</Step>
                <Step number={3}>{t('helpEnterExtras')}</Step>
                <Step number={4}>{t('helpEnterSaveLink')}</Step>
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg p-3 mt-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>💡 {t('tip')}</strong> {t('helpEnteringTip')}
                  </p>
                </div>
              </HelpSection>
            </>
          )}

          {/* Admin Help Content */}
          {isAdmin && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-ink mb-4">{t('helpAdminTitle')}</h2>
                <p className="text-ink-muted mb-6">{t('helpAdminDescription')}</p>
              </div>

              <HelpSection icon={Layers} title={t('helpClasses')}>
                <p>{t('helpClassesDescription')}</p>
              </HelpSection>

              <HelpSection icon={Trophy} title={t('helpManagingSeries')}>
                <p className="mb-3">{t('helpSeriesDescription')}</p>
                <Step number={1}>{t('helpCreateSeries')}</Step>
                <Step number={2}>{t('helpConfigureSeries')}</Step>
                <Step number={3}>{t('helpAddDrivers')}</Step>
                <Step number={4}>{t('helpAddRaces')}</Step>
                <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 mt-3">
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                    <strong>⚙️ {t('seriesSettings')}:</strong> {t('helpSeriesSettingsTip')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={Users} title={t('helpManagingDrivers')}>
                <p className="mb-3">{t('helpDriversDescription')}</p>
                <Step number={1}>{t('helpAddDriverManually')}</Step>
                <Step number={2}>{t('helpCopyDrivers')}</Step>
                <Step number={3}>{t('helpEditDriverInfo')}</Step>
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg p-3 mt-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>💡 {t('tip')}</strong> {t('helpDriversTip')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={Calendar} title={t('helpManagingRaces')}>
                <p className="mb-3">{t('helpRacesDescription')}</p>
                <Step number={1}>{t('helpCreateRace')}</Step>
                <Step number={2}>{t('helpAddResultsManually')}</Step>
                <p className="mt-3">📑 {t('helpCopyRace')}</p>
                <p className="mt-4"><strong>{t('helpImportingResults')}:</strong></p>
                <div className="ml-6 space-y-2 mt-2">
                  <p><strong>📊 {t('importFromExcel')}:</strong> {t('helpExcelImport')}</p>
                  <p><strong>🔗 {t('importFromMyRCM')}:</strong> {t('helpMyRCMImport')}</p>
                  <p><strong>🔗 LiveRC:</strong> {t('helpLiveRCImport')}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 rounded-lg p-3 mt-3">
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    <strong>✨ {t('tip')}</strong> {t('helpRacesTip')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={ClipboardList} title={t('helpEvents')}>
                <p className="mb-3">{t('helpEventsDescription')}</p>
                <Step number={1}>{t('helpEventCreate')}</Step>
                <Step number={2}>{t('helpEventEntryWindow')}</Step>
                <Step number={3}>{t('helpEventQuestions')}</Step>
                <div className="ml-6 space-y-2 mt-2">
                  <p><strong>🌐 {t('eventExternalEntry')}:</strong> {t('helpEventExternalEntry')}</p>
                  <p><strong>📋 {t('eventEntrantList')}:</strong> {t('helpEventEntrantList')}</p>
                  <p><strong>➕ {t('eventAddAllFromSeries')}:</strong> {t('helpAddAllClasses')}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg p-3 mt-3">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>📑 {t('eventCopy')}:</strong> {t('helpEventCopy')}
                  </p>
                </div>
              </HelpSection>

              <HelpSection icon={FileSpreadsheet} title={t('helpExcelFormat')}>
                <p className="mb-3">{t('helpExcelFormatDescription')}</p>
                <div className="bg-paper-alt rounded-lg p-4 mt-3">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="pb-2 text-accent">{t('columnDriver')}</th>
                        <th className="pb-2 text-accent">{t('columnPosition')}</th>
                        <th className="pb-2 text-accent">{t('columnQual')}</th>
                      </tr>
                    </thead>
                    <tbody className="text-ink-muted">
                      <tr>
                        <td className="pt-2">John Smith</td>
                        <td>1</td>
                        <td>3</td>
                      </tr>
                      <tr>
                        <td>Jane Doe</td>
                        <td>2</td>
                        <td>1</td>
                      </tr>
                      <tr>
                        <td>Mike Wilson</td>
                        <td>3</td>
                        <td>2</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </HelpSection>

              <HelpSection icon={Download} title={t('helpExportingData')}>
                <p className="mb-3">{t('helpExportDescription')}</p>
                <Step number={1}>{t('helpOpenExportDialog')}</Step>
                <Step number={2}>{t('helpSelectExportSeries')}</Step>
                <Step number={3}>{t('helpChooseExportLanguage')}</Step>
                <Step number={4}>{t('helpDownloadExcel')}</Step>
              </HelpSection>

              <HelpSection icon={Settings} title={t('helpSeriesSettingsDetail')}>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-ink">{t('totalRaces')}:</p>
                    <p className="text-sm">{t('helpTotalRacesDetail')}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{t('racesCounted')}:</p>
                    <p className="text-sm">{t('helpRacesCountedDetail')}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{t('calculateQualificationPoints')}:</p>
                    <p className="text-sm">{t('helpQualificationPointsDetail')}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{t('qualificationMaster')}:</p>
                    <p className="text-sm">{t('helpQualificationMasterDetail')}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{t('juniorChampionship')} / {t('seniorChampionship')}:</p>
                    <p className="text-sm">{t('helpAgeChampionshipsDetail')}</p>
                  </div>
                </div>
              </HelpSection>
            </>
          )}

          {/* Common Features */}
          <HelpSection icon={HelpCircle} title={t('helpCommonFeatures')}>
            <div className="space-y-2">
              <p><strong>🌍 {t('language')}:</strong> {t('helpLanguageDescription')}</p>
              <p><strong>📍 {t('country')}:</strong> {t('helpCountryDescription')}</p>
              <p><strong>📅 {t('selectYear')}:</strong> {t('helpYearDescription')}</p>
            </div>
          </HelpSection>

          {/* Support Information */}
          <div className="mt-8 bg-paper-alt rounded-lg p-6 border border-line">
            <h3 className="text-xl font-bold text-ink mb-3">{t('helpNeedMoreHelp')}</h3>
            <p className="text-ink-muted mb-3">{t('helpContactDescription')}</p>
            <div className="text-sm text-ink-muted">
              <p>{t('helpVersion')}: 1.0.0</p>
              <p>{t('helpSystemName')}: RC Racing Series Table</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-line p-4 bg-paper-alt">
          <button
            onClick={onClose}
            className="w-full bg-accent hover:opacity-90 text-accent-ink px-6 py-3 rounded-lg font-semibold"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HelpView;
